import { headerAndFooter as config } from '@config';
import Store, { actions } from '@store';

import HeadingsListener from './page-heading/headings-listener';
import {
  replacePageNumbering,
  deleteExistingPageNumbering,
  createPageNumbering
} from './page-numbering';
import { pagePropagationInstance } from './page-propagation';
import { createLayout } from './template';

module.exports = (function HeaderAndFooterFacade () {
  let settings = {};
  let hasChanges = false;
  let encodedId;
  let settingsLoaded = false;

  return {
    ...Backbone.Events,

    async initialize () {
      this.tool = this.tool || INKIVE.Tool.getInstance();
      this.canvas = this.canvas || this.tool.editor.canvas;

      this.size = {};
      this.pagePropagation = pagePropagationInstance;
      await this.pagePropagation.init();

      this._addEventListeners();
      this._addHeadingsListeners();
      this._initializeSettings();
    },

    _addHeadingsListeners () {
      this._headingsListener = new HeadingsListener();
      this._setHeadingSection();
    },

    _setHeadingSection () {
      const settings = INKIVE.EditorFacade.get('header_and_footer', 'range');

      this._headingsListener.setHeadingSection({
        id: settings.headingSection,
        level: settings.headingLevel
      });
    },

    settings () {
      return { ...settings };
    },

    _setSettings (data) {
      settings = { ...data };
    },

    async workerSettings () {
      // TODO: Would like to expose this somehow for debugging
      return await this.pagePropagation.getSettings();
    },

    async propagation ({ pageId } = {}) {
      return await this.pagePropagation.propagateContent({ pageId });
    },

    async _initializeSettings (data) {
      if (settingsLoaded || (!data && !INKIVE.CompanyFactory)) return;

      if (!data && INKIVE.CompanyFactory) {
        data = await INKIVE.CompanyFactory.fetchProjectData();
      }

      encodedId = data.encoded_id;

      const allPages = Array.isArray(data.pages) ? data.pages : INKIVE.ProjectFacade.get('designs');
      const pages = allPages.map(({ id, page_number: pageNumber }, index) => ({
        id,
        pageNumber: pageNumber ?? index + 1
      }));
      const metadata = data.metadata?.layout;

      if (metadata?.length > 0 && pages?.length > 0) {
        await this.pagePropagation.initializeSettings({
          id: data.encoded_id,
          metadata,
          pages
        });

        this._setHeadingSection();

        settingsLoaded = true;
      }
    },

    _isActive () {
      const svgRoot = document.getElementById('svgroot');
      return svgRoot.classList.contains('header-and-footer-active');
    },

    _addEventListeners () {
      this.pagePropagation.on('settingsUpdated', (initialized => data => {
        this._setSettings(data);
        if (initialized) this._setHeadingSection();

        initialized = true;
      })(false));

      INKIVE.EditorFacade.on('editor.loaded', async () => {
        const data = await INKIVE.CompanyFactory.fetchProjectData();

        data && this._initializeSettings(data);

        this.updateHeaderAndFooterSize();
      });

      INKIVE.ProjectFacade.on('project.fetched', async data => {
        // Need to stop invalid data re-entering here as page information hasn't finished loading
        if (data.encoded_id === encodedId) return;

        data && this._initializeSettings(data);
      });

      INKIVE.ProjectFacade.on('design.added', data => {
        if (!this._isEnabled() || !this._isActive()) return;

        this.beforeChangePage();
        // INKIVE.HeaderAndFooterFactory.updateSettings();
      });

      INKIVE.ProjectFacade.on('design.copied', data => {
        if (!this._isEnabled() || !this._isActive()) return;

        this.beforeChangePage();
      });

      INKIVE.ProjectFacade.on('design.removing', data => {
        if (!this._isEnabled() || !this._isActive()) return;

        this.beforeChangePage();
      });

      INKIVE.ProjectFacade.on('refresh_page_info', () => {
        if (!this._isEnabled() || !this._isActive()) return;

        this.beforeChangePage();
        // INKIVE.HeaderAndFooterFactory.updateSettings();
      });

      INKIVE.DesignFacade.on('template.update.size', () => {
        if (!this._isEnabled()) return;
        if (this._isActive()) this.canvas.runExtensions('saveHeaderAndFooterState');

        this.updateHeaderAndFooterSize();
      });

      INKIVE.EditorFacade.on('page.move_to', (data) => {
        if (!this._isEnabled()) return;
        if (this._isActive()) this.canvas.runExtensions('saveHeaderAndFooterState');

        this.updateHeaderAndFooterSize();
      });
    },

    _updatePageNumbering () {
      const settings = INKIVE.EditorFacade.get('header_and_footer', 'page');
      const rangeSettings = INKIVE.EditorFacade.get('header_and_footer', 'range');

      if (settings.pageNumbering) {
        replacePageNumbering(settings?.pageNumbering);
      } else if (!rangeSettings.numberingSection) {
        deleteExistingPageNumbering();
      }
    },

    _applyLimitToSize (size) {
      return Math.min(
        Math.max(Number(size), config.size.min),
        config.size.max
      );
    },

    _isEnabled () {
      return INKIVE.EditorFacade.get('header_and_footer')?.ranges?.length > 0;
    },

    _hasHeaderAndFooterLayer () {
      const layers = this.canvas.getCurrentDrawing().all_layers;

      return layers
        .some(([name]) => name === config.main.layer);
    },

    open ({ type } = {}) {
      if (!this._hasHeaderAndFooterLayer()) this._buildHeaderAndFooter();

      const extensions = {
        headerAndFooter: config.main.extension,
        headerView: config.header.extension,
        footerView: config.footer.extension
      };

      const extension = extensions[type];
      if (!extension) return;

      this.canvas.runExtensions(extension);

      hasChanges = true;
    },

    edit () {
      hasChanges = true;
    },

    async save () {
      if (!hasChanges) return;

      const pageId = this.getCurrentPageId();
      INKIVE.hasEditorUnsavedChanges = true;
      hasChanges = false;

      this.canvas.runExtensions('saveHeaderAndFooterState');

      await this.propagation({ pageId });
      await INKIVE.Helpers.delay(500); // Remove when fixed the issue HF saving is not async
      await new Promise(resolve => {
        INKIVE.EditorFacade.once('heading-propagation-finished', () => resolve());
        INKIVE.EditorFacade.trigger('header-and-footer-saved');
      });
    },

    close () {
      this.canvas.runExtensions('hideHeaderAndFooter');
    },

    hasChanges () { return hasChanges; },

    beforeChangePage () {
      // this.close();
    },

    updateHeaderAndFooterSize () {
      const rangeSettings = INKIVE.EditorFacade.get('header_and_footer', 'range');

      const headerSize = rangeSettings?.headerSize || config.size.initial;
      const footerSize = rangeSettings?.footerSize || config.size.initial;

      this.setHeaderSize({ size: headerSize });
      this.setFooterSize({ size: footerSize });
    },

    getSize () {
      if (!this.size.header) this.updateHeaderAndFooterSize();
      return this.size;
    },

    getCurrentPageId () {
      return INKIVE.PageFactory.getCurrentPageId();
    },

    /**
     * Only accepting percentage at this stage
     */
    setHeaderSize ({ size = config.size.initial, unit = '%' } = {}) {
      const rangeSettings = INKIVE.EditorFacade.get('header_and_footer', 'range');
      const newSize = size ?? rangeSettings.headerSize;

      this._adjustHeaderSize({ size: newSize });
      Store.dispatch(actions.editor.setHeaderSize({ size: newSize }));
    },

    _adjustHeaderSize ({ size: rawSize = config.size.initial, unit = '%' } = {}) {
      const size = this._applyLimitToSize(rawSize);
      const width = this.canvas.contentW;
      const height = this.canvas.contentH;
      const headerHeight = height * (size / 100);
      const element = document.getElementById(config.header.rectId);

      this.size.header = {
        x: 0,
        y: 0,
        width,
        height: headerHeight
      };

      element && svgedit.utilities.assignAttributes(element, {
        ...this.size.header,
        'data-size': size
      });
    },

    setFooterSize ({ size = config.size.initial, unit = '%' } = {}) {
      const rangeSettings = INKIVE.EditorFacade.get('header_and_footer', 'range');
      const newSize = size ?? rangeSettings.footerSize;

      this._adjustFooterSize({ size: newSize });
      Store.dispatch(actions.editor.setFooterSize({ size: newSize }));
    },

    _adjustFooterSize ({ size: rawSize = config.size.initial, unit = '%' } = {}) {
      const size = this._applyLimitToSize(rawSize);
      const width = this.canvas.contentW;
      const height = this.canvas.contentH;
      const footerHeight = height * (size / 100);
      const footerY = height - footerHeight;
      const element = document.getElementById(config.footer.rectId);

      this.size.footer = {
        x: 0,
        y: footerY,
        width,
        height: footerHeight
      };

      element && svgedit.utilities.assignAttributes(element, {
        ...this.size.footer,
        'data-size': size
      });
    },

    propagatePageNumberingSettings () {
      const rangeSettings = INKIVE.EditorFacade.get('header_and_footer', 'range');
      const pageSettings = INKIVE.EditorFacade.get('header_and_footer', 'page');

      const existingPageNumbering = document.querySelector(config.pageNumbering.attribute.selector);
      const hasExistingPageNumbering = !!existingPageNumbering;
      const sectionId = config[rangeSettings.numberingSection]?.id;
      const isElementInTheSameSection = !!existingPageNumbering?.closest(`#${sectionId}`);

      if (!rangeSettings.numberingSection) {
        if (hasExistingPageNumbering) deleteExistingPageNumbering();

        return;
      }

      if (hasExistingPageNumbering && isElementInTheSameSection) {
        this._updatePageNumbering();
        return;
      }

      const extension = rangeSettings.numberingSection === 'header' ? 'showHeaderView' : 'showFooterView';

      this.canvas.runExtensions('showHeaderAndFooter');
      this.canvas.runExtensions(extension);

      createPageNumbering({
        section: rangeSettings.numberingSection,
        numbering: pageSettings.pageNumbering
      });
    },

    async setRange (metadata) {
      this.close();

      const pages = INKIVE.ProjectFacade.get('designs').map(page => ({
        id: page.id,
        pageNumber: page.page_number
      }));

      await this.pagePropagation.setRange(
        metadata,
        pages
      );

      this._headingsListener.setHeadingSection({
        id: metadata.heading_section,
        level: metadata.heading_level
      });

      setTimeout(() => {
        this.propagatePageNumberingSettings();
        this.open({ type: 'headerAndFooter' });
        this._headingsListener.propagatePageHeadingSettings();
      });
    },

    async setInitialRange () {
      const settings = INKIVE.EditorFacade.get('header_and_footer', 'settings');
      if (!settings || settings.ranges?.length > 0) return;

      const pages = INKIVE.ProjectFacade.get('designs').map(page => ({
        id: page.id,
        pageNumber: page.page_number
      }));
      const initialRange = {
        footer_size_percentage: config.size.initial,
        header_size_percentage: config.size.initial,
        odd_and_even: false,
        range: 'all'
      };

      await this.pagePropagation.setRange(
        initialRange,
        pages
      );
    },

    _appendTemplate () {
      const layer = this._createLayoutLayer();

      createLayout(this.canvas)(layer, { size: this.size });
    },

    _createLayoutLayer () {
      const canvasLayerPosition = this.canvas.getCurrentDrawing().all_layers
        .findIndex(([name, element]) => element.classList.contains('main'));

      this.canvas.createLayer(config.main.layer);
      this.canvas.setCurrentLayerPosition(canvasLayerPosition);

      const element = this.canvas.getCurrentDrawing().getCurrentLayer();
      element.classList.add(config.main.class);

      return element;
    },

    _buildHeaderAndFooter () {
      const element = document.querySelector(`#svgcontent > g.${config.main.class}`);

      this.setHeaderSize({ size: document.getElementById(config.header.rectId)?.dataset?.size });
      this.setFooterSize({ size: document.getElementById(config.footer.rectId)?.dataset?.size });

      if (!element) this._appendTemplate();

      this.initialized = true;

      this.setInitialRange();
    },

    async createPage ({ skip_range: skipRange = false, page_id: pageId, page_number: pageNumber }) {
      if (this._isEnabled()) {
        return await this.pagePropagation.createPage({
          pageId,
          pageNumber
        }, { skipRange });
      }
    },

    async duplicatePage (args) {
      if (this._isEnabled()) {
        return await this.pagePropagation.duplicatePage(args);
      }
    },

    async deletePage ({ pageId, pageNumber }) {
      if (this._isEnabled()) {
        const response = await this.pagePropagation.deletePage({ pageId, pageNumber });
        await INKIVE.Helpers.delay(500);
        return response;
      }
    },

    async orderChanged ({ pageId, previousPageNumber, nextPageNumber, previousOrder }) {
      if (this._isEnabled()) {
        return await this.pagePropagation.orderChanged({
          page: {
            pageId,
            pageNumber: nextPageNumber
          },
          previousPageNumber,
          nextPageNumber,
          previousOrder
        });
      }
    },

    async extractTextElements ({ id, content }) {
      return await this.pagePropagation.extractTextElements({ id, content });
    }
  };
})();
